import { buttonVariants } from "@/components/ui/button";
import { useMixpanel } from "@/hooks/useMixpanel";
import { cn } from "@/lib/utils";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

interface SidebarNavProps extends React.HTMLAttributes<HTMLElement> {
  items: {
    href: string;
    title: string;
  }[];
}

export default function AccountSidebarNav({ className, items, ...props }: SidebarNavProps) {
  const location = useLocation();
  const { trackEvent } = useMixpanel();
  const pathname = location.pathname;
  const [searchParams] = useSearchParams();
  const bcApplicationId = searchParams.get("bc-request-id");
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(
    () => items.find((item) => item.href === pathname) || items[0],
  );

  useEffect(() => {
    const newItem = items.find((item) => item.href === pathname || pathname.startsWith(item.href));
    if (newItem) {
      if (bcApplicationId) {
        setSelectedItem(items[1]);
      } else {
        setSelectedItem(newItem);
      }
    }
  }, [pathname, items]);

  return (
    <nav
      className={cn(
        "flex flex-col md:flex-row md:space-x-2 lg:flex-col lg:space-x-0 lg:space-y-1",
        className,
      )}
      {...props}
    >
      {/* Mobile dropdown */}
      <div className="block lg:hidden">
        <button
          className={cn(buttonVariants({ variant: "outline" }), "w-full justify-between")}
          onClick={() => setDropdownOpen(!isDropdownOpen)}
        >
          {selectedItem.title}
          <span className="ml-2">{isDropdownOpen ? <ChevronUp /> : <ChevronDown />}</span>
        </button>
        {isDropdownOpen && (
          <div className="mt-2 space-y-1 rounded-md bg-muted shadow-lg">
            {items.map((item) => (
              <Link
                to={item.href}
                key={item.href}
                className={cn(
                  buttonVariants({ variant: "ghost" }),
                  (bcApplicationId
                    ? items[1].href === item.href
                    : pathname === item.href || pathname.startsWith(item.href))
                      ? "bg-secondary text-secondary-foreground hover:underline"
                      : "hover:bg-primary-foreground hover:underline",
                  "flex w-full justify-start px-4 text-left ",
                )}
                onClick={() => {
                  setSelectedItem(item);
                  setDropdownOpen(false);
                  trackEvent("Account: Sidebar Item Clicked", { ...item, page: item.title });
                }}
              >
                {item.title}
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Desktop view */}
      <div className="hidden flex-col space-y-1 lg:flex">
        {items.map((item) => (
          <Link
            key={item.href}
            to={item.href}
            className={cn(
              buttonVariants({ variant: "ghost" }),
              pathname === item.href
                ? "bg-muted hover:bg-muted"
                : "hover:bg-transparent hover:underline",
              "justify-start",
            )}
            onClick={() =>
              trackEvent(`${item.title}: Sidebar Item Clicked`, { ...item, page: "Account" })
            }
          >
            {item.title}
          </Link>
        ))}
      </div>
    </nav>
  );
}
